// src/pages/outsourcing_management/requisitions/RequisitionDetailDialog.tsx
//
// EE-207 Phase 4 (#13/#14/#15/#16) - detail view for a single Purchase
// Requisition: header, item lines with server-computed totals, status chip,
// status-gated workflow actions, the edit-after-submit limited form, and the
// audit-trail timeline.
//
// Implemented as a Dialog (not a routed page) - this mirrors the existing
// pattern for the pre-existing partner "Inquiry"/Requisition concept in this
// same area (OutsourcingManagementUI opens RequisitionModal, a Dialog, from
// a table row click) rather than introducing a new `/requisitions/:id`
// route. useRequisition(id) is the single-GET query, which is the
// only one that returns a populated audit_trail (list rows omit it).
//
// Draft requisitions reuse RequisitionCreateModal for editing (full
// header+items form, plus Delete Draft) rather than a second draft form,
// per the Phase 3 note on that component. Anything past Draft uses the
// limited RequisitionEditableFieldsForm instead, gated by
// requisitionWorkflow.canEditFields.
import { FunctionComponent, useContext, useEffect, useState } from "react";
import { Button, Chip, Divider, Grid, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DetailDialogShell from "components/Dashboard/DetailDialogShell";
import Fact from "components/Dashboard/Fact";
import AppStateContext from "contexts/AppStateContext";
import { useToast } from "contexts/ToastContext";
import {
  RequisitionItemType,
  RequisitionStatusEnum,
  RequisitionType,
} from "types/RequisitionTypes";
import {
  useAcknowledgeRequisitionStatus,
  useCancelRequisitionLine,
  useRequisition,
  useRequisitionOptions,
} from "hooks/useRequisitions";
import {
  requisitionStatusLabel,
  REQUISITION_STATUS_COLORS,
} from "../constants/requisitionStatusColors";
import { reformatDate, reformatDateTime } from "util/DateUtil";
import RequisitionDetailItemsTable from "./RequisitionDetailItemsTable";
import RequisitionWorkflowActions from "./RequisitionWorkflowActions";
import RequisitionEditableFieldsForm from "./RequisitionEditableFieldsForm";
import RequisitionAuditTrail from "./RequisitionAuditTrail";
import RequisitionInquiryLinks from "./RequisitionInquiryLinks";
import RequisitionCreateModal from "./RequisitionCreateModal";
import RequisitionPromptDialog from "./RequisitionPromptDialog";
import { canEditFields } from "./requisitionWorkflow";
import { partSummary } from "./requisitionForm";

type PropsType = {
  open: boolean;
  requisitionId?: number;
  onClose: () => void;
  onDeleted?: () => void;
};

const formatDateOrDash = (date: string | null) => (date ? reformatDate(date) : "-");

const RequisitionDetailDialog: FunctionComponent<PropsType> = ({
  open,
  requisitionId,
  onClose,
  onDeleted,
}) => {
  const { token } = useContext(AppStateContext);
  const [showEditDraftModal, setShowEditDraftModal] = useState(false);

  const { data: requisition, isLoading } = useRequisition(token, requisitionId, {
    enabled: open && requisitionId !== undefined,
  });

  // Opening the requisition is the acknowledgement (EE-253) - there is no
  // click on the indicator itself. Fires once the loaded requisition actually
  // has something pending, so a reopen of an already-acknowledged one is not
  // a request; the mutation is idempotent server-side either way, and the
  // flag it clears is global, so nothing here is per-user.
  const { mutate: acknowledgeStatus } = useAcknowledgeRequisitionStatus(token);
  const pendingAckId =
    open && requisition?.status_ack_pending ? requisition.id : undefined;
  useEffect(() => {
    if (pendingAckId !== undefined) acknowledgeStatus(pendingAckId);
  }, [pendingAckId, acknowledgeStatus]);

  const handleDraftSaved = () => {
    setShowEditDraftModal(false);
  };

  const handleDraftDeleted = () => {
    setShowEditDraftModal(false);
    onDeleted?.();
    onClose();
  };

  return (
    <>
      <DetailDialogShell
        open={open}
        onClose={onClose}
        title={`Requisition ${
          requisition ? requisition.requisition_no ?? `#${requisition.id}` : ""
        }`}
        isLoading={isLoading || !requisition}
      >
        {requisition && (
          <RequisitionDetailContent
            requisition={requisition}
            onEditDraft={() => setShowEditDraftModal(true)}
            onClose={onClose}
          />
        )}
      </DetailDialogShell>

      {requisition && (
        <RequisitionCreateModal
          open={showEditDraftModal}
          onClose={() => setShowEditDraftModal(false)}
          requisition={requisition}
          onSaved={handleDraftSaved}
          onDeleted={handleDraftDeleted}
        />
      )}
    </>
  );
};

type ContentPropsType = {
  requisition: RequisitionType;
  onEditDraft: () => void;
  onClose: () => void;
};

const RequisitionDetailContent: FunctionComponent<ContentPropsType> = ({
  requisition,
  onEditDraft,
  onClose,
}) => {
  const colors = REQUISITION_STATUS_COLORS[requisition.status];
  const isDraft = requisition.status === RequisitionStatusEnum.DRAFT;
  const { token } = useContext(AppStateContext);
  const { addToast } = useToast();
  const { data: options } = useRequisitionOptions(token);
  const [cancelling, setCancelling] = useState<RequisitionItemType>();
  const label = requisition.requisition_no ?? `#${requisition.id}`;
  const cancelLine = useCancelRequisitionLine(token, {
    onSuccess: () => {
      addToast({
        type: "success",
        message: `Line ${cancelling?.line_no ?? ""} of ${label} cancelled`,
      });
      setCancelling(undefined);
    },
    onError: (err) =>
      addToast({
        type: "error",
        message: `Could not cancel the line: ${err}`,
        keep: true,
      }),
  });
  // One line at a time, once submitted and while another line stays open -
  // the last one goes with the requisition (Cancel Requisition).
  const openLines = requisition.items.filter(
    (item) => item.line_state !== "CANCELLED"
  ).length;
  const canCancelLines =
    ![
      RequisitionStatusEnum.DRAFT,
      RequisitionStatusEnum.DONE,
      RequisitionStatusEnum.CLOSED,
    ].includes(requisition.status) && openLines > 1;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={8}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.75rem" }}>
          <Chip
            data-testid="status-token"
            label={requisitionStatusLabel(requisition.status)}
            sx={{
              fontWeight: "bold",
              border: colors?.border,
              color: colors?.color,
              background: colors?.background,
            }}
          />
          {isDraft && (
            <Button size="small" startIcon={<EditIcon />} onClick={onEditDraft}>
              Edit Draft
            </Button>
          )}
        </div>

        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Fact label="Type">
            {requisition.type_label ?? requisition.type_code ?? "-"}
          </Fact>
          <Fact label="Purpose">
            {requisition.purpose_label ?? requisition.purpose_code ?? "-"}
          </Fact>
          <Fact label="Requester">
            {requisition.requester_name ?? requisition.requester_email ?? "-"}
          </Fact>
          <Fact label="Sales Order / Parts">
            {requisition.sales_order_id ? (
              <a href={`/ordrestyring/${requisition.sales_order_id}`}>
                {requisition.sales_order_no ?? `#${requisition.sales_order_id}`}
              </a>
            ) : (
              "-"
            )}
            {/* Every part the lines point at - a requisition covers several
                parts of one order, and the header names only the first. */}
            {(() => {
              const parts = partSummary(
                requisition.items,
                requisition.order_item_name
              );
              return parts ? ` / ${parts}` : null;
            })()}
          </Fact>
          <Fact label="Supplier">{requisition.supplier_name ?? "-"}</Fact>
          <Fact label="Expected Delivery Date">
            {formatDateOrDash(requisition.required_by_date)}
          </Fact>
          <Fact label="Planned Delivery Date">
            {formatDateOrDash(requisition.estimated_delivery_date)}
          </Fact>
          {requisition.status_changed_at && (
            <Fact label="Status Changed">
              {reformatDateTime(requisition.status_changed_at)}
            </Fact>
          )}
          <Fact label="Created">
            {requisition.created ? reformatDateTime(requisition.created) : "-"}
            {requisition.created_by ? ` (${requisition.created_by})` : ""}
          </Fact>
          {requisition.comments && (
            <Fact label="Comments" full>
              <span style={{ whiteSpace: "pre-wrap" }}>
                {requisition.comments}
              </span>
            </Fact>
          )}
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          Item Lines
        </Typography>
        <RequisitionDetailItemsTable
          typeCode={requisition.type_code}
          items={requisition.items}
          onCancelLine={canCancelLines ? setCancelling : undefined}
        />
        <RequisitionPromptDialog
          open={cancelling !== undefined}
          title={`Cancel line ${cancelling?.line_no ?? ""}`}
          description="The line leaves Awaiting Inquiry, or an Inquiry not yet sent. On an Inquiry already sent it stays, flagged for the buyer. The rest of the requisition goes on. This cannot be undone."
          confirmLabel="Cancel Line"
          cancelLabel="Back"
          destructive
          reason={{
            options,
            label: "Cancellation Reason",
            missingText: "A cancellation reason is required",
          }}
          isSubmitting={cancelLine.isLoading}
          onClose={() => setCancelling(undefined)}
          onConfirm={(comment, reasonCode) =>
            cancelling &&
            cancelLine.mutate({
              id: requisition.id,
              lineId: cancelling.id,
              reasonCode: reasonCode!,
              comment: comment || undefined,
            })
          }
        />

        {canEditFields(requisition.status) && (
          <>
            <Divider sx={{ my: 2 }} />
            <RequisitionEditableFieldsForm requisition={requisition} />
          </>
        )}
      </Grid>

      <Grid item xs={12} md={4}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          Actions
        </Typography>
        <RequisitionWorkflowActions requisition={requisition} />

        <Divider sx={{ my: 2 }} />

        {/* #17: the Inquiry this requisition spawned on submit (EE-253) -
            where it is approved, sent to the supplier and stepped through the
            rest of the flow. Navigating away unmounts this page, so close the
            dialog first. */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          Inquiries
        </Typography>
        <RequisitionInquiryLinks
          inquiries={requisition.inquiries}
          status={requisition.status}
          onNavigate={onClose}
        />

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          Audit Trail
        </Typography>
        <RequisitionAuditTrail auditTrail={requisition.audit_trail} />
      </Grid>
    </Grid>
  );
};

export default RequisitionDetailDialog;
